import { Button, Input, Typography } from 'antd';
import React from 'react';

import { MObywatelRequest } from './types';

const { Text } = Typography;

interface RequestActionsProps {
  request: MObywatelRequest;
  codeInputs: { [key: string]: string };
  setCodeInputs: (inputs: { [key: string]: string }) => void;
  actionLoading: { [key: string]: boolean };
  onCodeSubmit: (requestId: string) => void;
  onConfirmVerification: (requestId: string, accepted: boolean) => void;
  onShowUserModal: (requestId: string) => void;
  onResetVerification: (requestId: string) => void;
}

export const RequestActions: React.FC<RequestActionsProps> = ({
  request,
  codeInputs,
  setCodeInputs,
  actionLoading,
  onCodeSubmit,
  onConfirmVerification,
  onShowUserModal,
  onResetVerification,
}) => {
  const canShowModal = [
    'instructions_shown',
    'waiting_for_code',
    'code_expired',
    'code_provided',
    'user_inactive',
  ].includes(request.status);
  const canReset = request.status !== 'completed';

  // Additional admin actions
  const renderAdditionalActions = () => {
    const additionalActions = [];

    if (canShowModal) {
      additionalActions.push(
        <Button
          key="show-modal"
          size="small"
          type="default"
          onClick={() => onShowUserModal(request.id)}
          loading={actionLoading[request.id]}
          className="w-full sm:w-auto">
          ⏰ Pokaż modal użytkownikowi
        </Button>
      );
    }

    if (canReset) {
      additionalActions.push(
        <Button
          key="reset"
          size="small"
          type="default"
          danger
          onClick={() => onResetVerification(request.id)}
          loading={actionLoading[request.id]}
          className="w-full sm:w-auto">
          🔄 Reset weryfikacji
        </Button>
      );
    }

    return additionalActions;
  };
  if (request.status === 'waiting_for_code' || request.status === 'code_expired') {
    const additionalActions = renderAdditionalActions();
    return (
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="6-cyfrowy kod"
            value={codeInputs[request.id] || ''}
            onChange={(e) => setCodeInputs({ ...codeInputs, [request.id]: e.target.value })}
            maxLength={6}
            className="w-full sm:w-28"
            size="small"
          />
          <Button
            type="primary"
            size="small"
            onClick={() => onCodeSubmit(request.id)}
            loading={actionLoading[request.id]}
            disabled={!codeInputs[request.id] || codeInputs[request.id].length !== 6}
            className="w-full sm:w-auto">
            Wyślij kod
          </Button>
        </div>
        {request.status === 'code_expired' && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2">
            <Text type="warning" className="text-xs">
              ⏰ Kod wygasł - wygeneruj nowy w aplikacji mObywatel
            </Text>
          </div>
        )}
        {additionalActions.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 border-t pt-2">{additionalActions}</div>
        )}
      </div>
    );
  }

  if (request.status === 'code_provided') {
    const additionalActions = renderAdditionalActions();
    return (
      <div className="space-y-2">
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <Text strong className="text-sm">
            Kod: {request.verificationCode}
          </Text>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="primary"
            size="small"
            onClick={() => onConfirmVerification(request.id, true)}
            loading={actionLoading[request.id]}
            className="w-full sm:w-auto">
            ✓ Zatwierdź
          </Button>
          <Button
            danger
            size="small"
            onClick={() => onConfirmVerification(request.id, false)}
            loading={actionLoading[request.id]}
            className="w-full sm:w-auto">
            ✗ Odrzuć
          </Button>
        </div>
        {additionalActions.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 border-t pt-2">{additionalActions}</div>
        )}
      </div>
    );
  }

  if (request.status === 'completed') {
    return (
      <div className="bg-green-50 border border-green-200 rounded p-2">
        <Text className="text-green-700 text-xs">✓ Zatwierdzone</Text>
      </div>
    );
  }

  if (request.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-2">
        <Text className="text-red-700 text-xs">✗ Odrzucone</Text>
      </div>
    );
  }

  if (request.status === 'awaiting_user_response') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
        <Text className="text-yellow-700 text-xs">⏰ Użytkownik widzi modal - czeka na odpowiedź</Text>
      </div>
    );
  }

  if (request.status === 'user_inactive') {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded p-2">
        <Text className="text-purple-700 text-xs">💤 Użytkownik nieaktywny - zostanie wyczyszczony</Text>
      </div>
    );
  }

  // Show only additional actions if no main actions are available
  const additionalActions = renderAdditionalActions();
  if (additionalActions.length > 0) {
    return (
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">{additionalActions}</div>
      </div>
    );
  }

  return null;
};
